import { fetchJSON as fetchAPI } from './client';

export type PhilosophyPillar = {
  key: string;
  name: string;
  description: string;
};

export type DNAField = {
  key: string;
  name: string;
  data_type: string;
  description?: string;
  score?: number;
};

export type DNALayerSpec = {
  key: string;
  name: string;
  description: string;
  fields: DNAField[];
  required: boolean;
};

export type KnowledgeGraphEntity = {
  key: string;
  name: string;
  entity_type: string;
  connections?: string[];
};

export type RelationshipType = {
  key: string;
  name: string;
  description: string;
  directional: boolean;
};

export type AIContextRequirement = {
  key: string;
  name: string;
  source: string;
  mandatory: boolean;
  description?: string;
};

export type LearningTrigger = {
  key: string;
  name: string;
  description: string;
  updates_layers?: string[];
};

export type MarketplaceDNAProfile = {
  asin: string;
  parent_asin?: string;
  marketplace: string;
  brand?: string;
  category?: string;
  lifecycle_stage: string;
  overall_score: number;
  layers: Record<string, number>;
  last_updated: string;
};

export type KnowledgeGraphEdge = {
  from: string;
  to: string;
  relationship: string;
  strength?: number;
};

export type EntityModelEntry = {
  entity: string;
  description: string;
  attributes: string[];
};

export type ContextValidationResult = {
  asin: string;
  complete: boolean;
  missing?: string[];
  layer_scores?: Record<string, number>;
  evaluated_at: string;
};

export type DNABibleDashboard = {
  bible_id: string;
  tenant_id: string;
  version: string;
  depends_on: string[];
  dna_layers: number;
  graph_entities: number;
  relationship_types: number;
  ai_context_requirements: number;
  learning_triggers: number;
  sample_profiles: number;
  generated_at: string;
};

export type DNABibleStatus = {
  bible_id: string;
  version: string;
  depends_on: string[];
  dna_layers: number;
  graph_entities: number;
  relationship_types: number;
  ai_context_requirements: number;
  learning_triggers: number;
  single_source: boolean;
  status: string;
};

const MARKETINTEL_BASE = import.meta.env.VITE_MARKETINTEL_API ?? '/api/v1/marketintel';

function fetchMarketIntel<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(`${MARKETINTEL_BASE}${path}`, init);
}

export const marketplaceDNABibleApi = {
  dashboard: () => fetchMarketIntel<DNABibleDashboard>('/dna-bible/dashboard'),
  philosophy: () => fetchMarketIntel<{ pillars: PhilosophyPillar[] }>('/dna-bible/philosophy').then((r) => r.pillars),
  layers: () => fetchMarketIntel<{ layers: DNALayerSpec[] }>('/dna-bible/layers').then((r) => r.layers),
  layer: (key: string) => fetchMarketIntel<DNALayerSpec>(`/dna-bible/layers/${key}`),
  profiles: () => fetchMarketIntel<{ profiles: MarketplaceDNAProfile[] }>('/dna-bible/profiles').then((r) => r.profiles),
  profile: (asin: string) => fetchMarketIntel<MarketplaceDNAProfile>(`/dna-bible/profiles/${asin}`),
  graphEntities: () => fetchMarketIntel<{ entities: KnowledgeGraphEntity[] }>('/dna-bible/graph/entities').then((r) => r.entities),
  graphEdges: () => fetchMarketIntel<{ edges: KnowledgeGraphEdge[] }>('/dna-bible/graph/edges').then((r) => r.edges),
  relationshipTypes: () =>
    fetchMarketIntel<{ relationship_types: RelationshipType[] }>('/dna-bible/graph/relationship-types').then((r) => r.relationship_types),
  aiContext: () => fetchMarketIntel<{ requirements: AIContextRequirement[] }>('/dna-bible/ai-context').then((r) => r.requirements),
  learningTriggers: () => fetchMarketIntel<{ triggers: LearningTrigger[] }>('/dna-bible/learning-triggers').then((r) => r.triggers),
  entityModel: () => fetchMarketIntel<{ entities: EntityModelEntry[] }>('/dna-bible/entity-model').then((r) => r.entities),
  validateContext: (body: { asin: string; provided: Record<string, boolean> }) =>
    fetchMarketIntel<ContextValidationResult>('/dna-bible/validate-context', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  status: () => fetchMarketIntel<DNABibleStatus>('/dna-bible/status'),
};
